/**
 * A 2:3 poster tile.
 *
 * Panel artwork is unreliable in two different ways: `logoUrl` is often null,
 * and when it is present the host 404s or hangs a good fraction of the time.
 * Both cases have to land on the same readable fallback, so the image is only
 * revealed once it has actually decoded.
 */

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { progressRatio } from '../lib/format';
import { SafeImage } from './SafeImage';

interface PosterProps {
  name: string;
  imageUrl: string | null;
  subtitle?: ReactNode;
  corner?: ReactNode;
  positionS?: number;
  durationS?: number;
  onClick?: () => void;
}

export function Poster({ name, imageUrl, subtitle, corner, positionS, durationS, onClick }: PosterProps) {
  const [loaded, setLoaded] = useState(false);

  // A new URL on a recycled tile must not inherit the previous one's loaded state.
  useEffect(() => {
    setLoaded(false);
  }, [imageUrl]);

  const ratio = positionS && durationS ? progressRatio(positionS, durationS) : 0;

  return (
    <button type="button" className="poster" onClick={onClick}>
      <div className="poster__frame">
        {imageUrl ? (
          <SafeImage
            src={imageUrl}
            style={{ opacity: loaded ? 1 : 0, transition: 'opacity var(--dur)' }}
            onLoad={() => setLoaded(true)}
          />
        ) : null}
        {!imageUrl || !loaded ? <span className="poster__fallback">{name}</span> : null}
        {corner ? <span className="poster__corner">{corner}</span> : null}
        {ratio > 0 ? (
          <span className="poster__progress">
            <span style={{ width: `${Math.round(ratio * 100)}%` }} />
          </span>
        ) : null}
      </div>
      <div className="poster__name">{name}</div>
      {subtitle ? <div className="poster__sub">{subtitle}</div> : null}
    </button>
  );
}
